import { AudioTrackId } from '@breath-lab/types';

/**
 * Returns the countdown AudioTrackId for a breath count number (1–30),
 * or null if out of range. Port of AudioHelpers.getAudioFilepathForInteger().
 */
export function getCountdownAudioTrackId(n: number): AudioTrackId | null {
  if (n < 1 || n > 30) return null;
  const key = `n${String(n).padStart(2, '0')}` as keyof typeof AudioTrackId;
  return AudioTrackId[key] ?? null;
}

/**
 * Returns the inhale breathing sound track closest to the given duration.
 * durationMs: duration in milliseconds. isUjjayi: use Ujjayi-specific tracks.
 * Port of AudioHelpers.getInhaleAudioTrackForDuration().
 */
export function getInhaleAudioTrackForDuration(durationMs: number, isUjjayi = false): AudioTrackId {
  const sec = durationMs / 1000;
  if (isUjjayi) {
    if (sec < 3) return AudioTrackId.inhaleSoundUjjayi2Sec;
    return AudioTrackId.inhaleSoundUjjayi4Sec;
  }
  if (sec < 1) return AudioTrackId.inhaleSound300ms;
  if (sec < 3) return AudioTrackId.inhaleSound02Sec;
  if (sec < 5) return AudioTrackId.inhaleSound04Sec;
  if (sec < 7) return AudioTrackId.inhaleSound06Sec;
  if (sec < 10) return AudioTrackId.inhaleSound08Sec;
  if (sec < 14) return AudioTrackId.inhaleSound12Sec;
  if (sec < 24) return AudioTrackId.inhaleSound20Sec;
  return AudioTrackId.inhaleSound30Sec;
}

/**
 * Returns the exhale breathing sound track closest to the given duration.
 * durationMs: duration in milliseconds. isUjjayi: use Ujjayi-specific tracks.
 * Port of AudioHelpers.getExhaleAudioTrackForDuration().
 */
export function getExhaleAudioTrackForDuration(durationMs: number, isUjjayi = false): AudioTrackId {
  const sec = durationMs / 1000;
  if (isUjjayi) {
    if (sec < 3) return AudioTrackId.exhaleSoundUjjayi2Sec;
    return AudioTrackId.exhaleSoundUjjayi4Sec;
  }
  if (sec < 1) return AudioTrackId.exhaleSound300ms;
  if (sec < 3) return AudioTrackId.exhaleSound02Sec;
  if (sec < 5) return AudioTrackId.exhaleSound04Sec;
  if (sec < 7) return AudioTrackId.exhaleSound06Sec;
  if (sec < 10) return AudioTrackId.exhaleSound08Sec;
  if (sec < 14) return AudioTrackId.exhaleSound12Sec;
  if (sec < 24) return AudioTrackId.exhaleSound20Sec;
  return AudioTrackId.exhaleSound30Sec;
}

/**
 * Returns the bee-exhale breathing sound track closest to the given duration.
 * durationMs: duration in milliseconds.
 * Port of AudioHelpers.getBeeExhaleAudioTrackForDuration().
 */
export function getBeeExhaleAudioTrackForDuration(durationMs: number): AudioTrackId {
  if (durationMs < 16500) return AudioTrackId.exhaleSoundBee15Sec;
  return AudioTrackId.exhaleSoundBee30Sec;
}

/**
 * Returns the known duration (ms) for a given AudioTrackId, or null for tracks
 * whose duration is not statically known (bhastrikaExhale, bhastrikaInhale,
 * breathworkPrep2). Port of AudioHelpers.getAudioTrackDuration().
 */
export function getAudioTrackDurationMs(trackId: AudioTrackId): number | null {
  switch (trackId) {
    case AudioTrackId.anulomVilomPrep2: return 11472;
    case AudioTrackId.anulomVilomPrep3: return 12744;
    case AudioTrackId.bhastrikaExhale: return null; // unimplemented in Dart
    case AudioTrackId.bhastrikaInhale: return null; // unimplemented in Dart
    case AudioTrackId.bhastrikaPrep1: return 7680;
    case AudioTrackId.bhramariPrep1: return 10992;
    case AudioTrackId.breathworkClosing: return 2400;
    case AudioTrackId.breathworkPrep1: return 2448;
    case AudioTrackId.breathworkPrep2: return null; // unimplemented in Dart
    case AudioTrackId.chandraBedhanaPrep1: return 13848;
    case AudioTrackId.closeYourEyesAndCenterYourself: return 4728;
    case AudioTrackId.closeYourEyesSettleBodyCalmMind: return 6000;
    case AudioTrackId.exhaleCount8: return 5328;
    case AudioTrackId.exhaleLeft: return 983;
    case AudioTrackId.exhaleMurcha: return 3123;
    case AudioTrackId.exhaleRight: return 959;
    case AudioTrackId.exhaleSoundBee15Sec: return 15000;
    case AudioTrackId.exhaleSoundBee30Sec: return 30000;
    case AudioTrackId.exhaleSound02Sec: return 2000;
    case AudioTrackId.exhaleSound04Sec: return 4000;
    case AudioTrackId.exhaleSound06Sec: return 6000;
    case AudioTrackId.exhaleSound08Sec: return 8000;
    case AudioTrackId.exhaleSound12Sec: return 12000;
    case AudioTrackId.exhaleSound20Sec: return 20000;
    case AudioTrackId.exhaleSound30Sec: return 30000;
    case AudioTrackId.exhaleSound300ms: return 486;
    case AudioTrackId.exhaleSoundUjjayi2Sec: return 2000;
    case AudioTrackId.exhaleSoundUjjayi4Sec: return 4000;
    case AudioTrackId.exhaleThroughMouth: return 1295;
    case AudioTrackId.exhaleThroughNose: return 1343;
    case AudioTrackId.exhaleWord: return 695;
    case AudioTrackId.exhaleWordBee: return 2111;
    case AudioTrackId.exhaleWordFast: return 1080;
    case AudioTrackId.getReady: return 1559;
    case AudioTrackId.hold: return 743;
    case AudioTrackId.holdBreathCount7: return 4320;
    case AudioTrackId.holdFemale: return 437;
    case AudioTrackId.inFast: return 1056;
    case AudioTrackId.inhale70Percent: return 4392;
    case AudioTrackId.inhaleLeft: return 959;
    case AudioTrackId.inhaleMouth: return 1295;
    case AudioTrackId.inhaleNose: return 1343;
    case AudioTrackId.inhaleQuietlyCount4: return 5328;
    case AudioTrackId.inhaleRight: return 911;
    case AudioTrackId.inhaleSmoothly: return 1079;
    case AudioTrackId.inhaleSound02Sec: return 2000;
    case AudioTrackId.inhaleSound04Sec: return 4000;
    case AudioTrackId.inhaleSound06Sec: return 6000;
    case AudioTrackId.inhaleSound08Sec: return 8000;
    case AudioTrackId.inhaleSound12Sec: return 12000;
    case AudioTrackId.inhaleSound20Sec: return 20000;
    case AudioTrackId.inhaleSound30Sec: return 30000;
    case AudioTrackId.inhaleSoundUjjayi2Sec: return 2000;
    case AudioTrackId.inhaleSoundUjjayi4Sec: return 4000;
    case AudioTrackId.inhaleSound300ms: return 300;
    case AudioTrackId.inhaleTongue: return 1727;
    case AudioTrackId.inhaleWord: return 791;
    case AudioTrackId.inhaleWordFast: return 1056;
    case AudioTrackId.exhaleSoundKapalabhati: return 800;
    case AudioTrackId.kapalabhatiPrep3: return 9096;
    case AudioTrackId.keepChestOpenBodyRelaxedAlertCloseEyesDeepBreathsCenterFocus: return 8616;
    case AudioTrackId.khechariMudraPrep1: return 7968;
    case AudioTrackId.khechariMudraPrep2: return 3912;
    case AudioTrackId.khechariMudraPrep3: return 11880;
    case AudioTrackId.khechariMudraPrep4: return 10824;
    case AudioTrackId.kumbhakaPrep1: return 7176;
    case AudioTrackId.kumbhakaPrep2: return 8232;
    case AudioTrackId.kumbhakaPrep3: return 10800;
    case AudioTrackId.kumbhakaPrep4: return 7608;
    case AudioTrackId.maintainRhythmicBreathingPattern: return 6120;
    case AudioTrackId.meditationMusic: return 604839;
    case AudioTrackId.metronome: return 816;
    case AudioTrackId.murchaPrep1: return 5784;
    case AudioTrackId.murchaPrep2: return 11160;
    case AudioTrackId.murchaPrep3: return 13080;
    case AudioTrackId.n01: return 417;
    case AudioTrackId.n02: return 431;
    case AudioTrackId.n03: return 415;
    case AudioTrackId.n04: return 561;
    case AudioTrackId.n05: return 542;
    case AudioTrackId.n06: return 582;
    case AudioTrackId.n07: return 564;
    case AudioTrackId.n08: return 425;
    case AudioTrackId.n09: return 485;
    case AudioTrackId.n10: return 428;
    case AudioTrackId.n11: return 628;
    case AudioTrackId.n12: return 510;
    case AudioTrackId.n13: return 637;
    case AudioTrackId.n14: return 700;
    case AudioTrackId.n15: return 658;
    case AudioTrackId.n16: return 796;
    case AudioTrackId.n17: return 819;
    case AudioTrackId.n18: return 599;
    case AudioTrackId.n19: return 644;
    case AudioTrackId.n20: return 497;
    case AudioTrackId.n21: return 715;
    case AudioTrackId.n22: return 788;
    case AudioTrackId.n23: return 783;
    case AudioTrackId.n24: return 819;
    case AudioTrackId.n25: return 851;
    case AudioTrackId.n26: return 917;
    case AudioTrackId.n27: return 845;
    case AudioTrackId.n28: return 739;
    case AudioTrackId.n29: return 793;
    case AudioTrackId.n30: return 423;
    case AudioTrackId.nadiShodanaCompletion: return 6804;
    case AudioTrackId.nadiShodanaPrep1: return 11640;
    case AudioTrackId.nadiShodanaPrep2: return 13944;
    case AudioTrackId.nadiShodanaPrep3: return 15072;
    case AudioTrackId.outFast: return 912;
    case AudioTrackId.placePalmsOnKneesPrep: return 6936;
    case AudioTrackId.plaviniPrep1: return 5664;
    case AudioTrackId.plaviniPrep2: return 3744;
    case AudioTrackId.plaviniPrep3: return 5040;
    case AudioTrackId.roundClosing: return 3840;
    case AudioTrackId.roundStartPrep: return 2352;
    case AudioTrackId.samaVritti3: return 6912;
    case AudioTrackId.samaVritti4: return 7536;
    case AudioTrackId.samaVritti5: return 8400;
    case AudioTrackId.sitaliPrep1: return 11088;
    case AudioTrackId.sitaliPrep2: return 7488;
    case AudioTrackId.sitComfortableSpineStraightRelaxHandsOnKnees: return 11088;
    case AudioTrackId.sitComfortableSpineStraightShouldersRelaxedGazeForwardPrep: return 8760;
    case AudioTrackId.sitComfortableSpineStraightShouldersRelaxedLeftHandOnKneeChinMudraPrep: return 9576;
    case AudioTrackId.sitComfortablyInPositionOfYourChoiceWithYourSpineStraightAndShouldersRelaxedPrep: return 8760;
    case AudioTrackId.sitComfortablyOrLieDown: return 9408;
    case AudioTrackId.sitQuietlyEyesClosedBreathToItsNaturalRhythm: return 6744;
    case AudioTrackId.sitkariPrep1: return 7200;
    case AudioTrackId.sitkariPrep2: return 5496;
    case AudioTrackId.suryaBhedanaPrep1: return 13752;
    case AudioTrackId.swallowAndHold: return 1063;
    case AudioTrackId.takeAFewNaturalBreathsSettleYourMindAndBody: return 5064;
    case AudioTrackId.takeFewDeepBreathsPrepTalk: return 5544;
    case AudioTrackId.takeFewMinutesHowBodyFeelsCoolerRefreshedRelaxed: return 5520;
    case AudioTrackId.takeFewMinutesHowBodyFeelsEnergizedClearPresent: return 5736;
    case AudioTrackId.tummoPrep1: return 6936;
    case AudioTrackId.tummoPrep2: return 11976;
    case AudioTrackId.tummoPrep3: return 7896;
    case AudioTrackId.ujjayiPrep1: return 7560;
    case AudioTrackId.ujjayiPrep2: return 5568;
    case AudioTrackId.vishamaVrittiPrep1: return 6768;
    case AudioTrackId.vishamaVrittiPrep2: return 6528;
  }
}
